import { useEffect, useState } from "react";
import { Box, Typography, Paper, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { tokens } from "../../theme";
import {
  useAuthSlice,
  updateElementInAuthSlice,
} from "../../redux/slice/authSlice";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import { fullName } from "../../utils/Utility";
import ProfileImageUpload from "../operation/user/ProfileImageUpload";
import { DocumentItemInfo } from "../../redux/slice/product/createProductSlice";
import { saveUserProfilePicture } from "../../redux/slice/userSlice";
import OptionsPopup from "../common/OptionsPopup";
import { USER_ROLES } from "../../constants/applicationConstants";
import WarningPopupNew, { WarningPopupNewObjectProps } from "../common/WarningPopupNew";
import CompletePracticeRegistrationModal from "../operation/account/CompletePracticeRegistrationModal";
import { Cancel } from "@mui/icons-material";

export type UserprofilePictureObj = {
  userId: number;
  email: string;
  profileDocument?: DocumentItemInfo | null;
};

const TimePanel = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [time, setTime] = useState(dayjs());
  const { userInfo } = useAuthSlice();
  const dispatch = useAppDispatch();

  const [file, setFile] = useState<File | null>(null);
  const [initialSrc, setInitialSrc] = useState<string | undefined>(undefined);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);
  const isRestrictedUser = userInfo.userRole === USER_ROLES.ROLE_PRACTICE_USER_PRU;
  const [showPRUWarning, setShowPRUWarning] = useState<WarningPopupNewObjectProps>({ show: false, message: '' });

  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);

  const withPRUGuard = (fn: () => void) => () => {
    if (isRestrictedUser) {
      setShowPRUWarning({
        show: true,
        title: "Pre Registered User Warning",
        message: (
          <center>
            {isRestrictedUser && userInfo.associatedPracticeStatus === "PENDING"
              ?
              "You don't have access to this feature. Please complete the practice registration in order to use the full system functionality."
              : "Your practice submission is pending approval. Full access to the system will be granted once it has been reviewed and approved."}
          </center>
        ),
        variant: "warning",
        buttons: [
          ...(isRestrictedUser && userInfo.associatedPracticeStatus === "PENDING"
            ? [
              {
                name: "Cancel",
                color: "error" as const,
                icon: <Cancel sx={{ fontSize: "1rem" }} />,
                onClick: () => setShowPRUWarning({ show: false, message: '' }),
              },
            ]
            : [
              {
                name: "OK",
                color: "success" as const,
                onClick: () => setShowPRUWarning({ show: false, message: '' }),
              },
            ]),
          ...(isRestrictedUser && userInfo.associatedPracticeStatus === "PENDING"
          ? [
              {
                name: "Complete Registration",
                color: "success" as const,
                 onClick: () => {
                  setRegistrationModalOpen(true);
                  setShowPRUWarning({ show: false, message: "" });
                },
              },
            ]
          : []),
        ],
      });
      return;
    }
    fn();
  };

  useEffect(() => {
    const interval = setInterval(() => setTime(dayjs()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    refreshInitialSrcFromUser();
  }, [userInfo?.profileDocument]);

  const refreshInitialSrcFromUser = () => {
    const doc = (userInfo as any)?.profileDocument;
    if (doc?.content && doc?.fileType) {
      setInitialSrc(`data:${doc.fileType};base64,${doc.content}`);
    } else {
      setInitialSrc(undefined);
    }
  };

  const convertBase64 = (f: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(f);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });

  const stripDataUrlPrefix = (dataUrl: string) =>
    dataUrl.replace(/^data:[^;]+;base64,/, "");

  const handleProfileChange = (f: File | null) => {
    if (f === null) {
      setFile(null);
      setInitialSrc(undefined);
    }
  };

  const onSaveRequest = (freshFile: File) => {
    setPendingFile(freshFile);
    setIsConfirmationOpen(true);
  };

  const handleCancel = () => {
    setFile(null);
    refreshInitialSrcFromUser();
    setIsConfirmationOpen(false);
    setPendingFile(null);
  };

  const handleSaveProfilePicture = async () => {
    if (!pendingFile) {
      setIsConfirmationOpen(false);
      return;
    }

    try {
      let profileDocument: DocumentItemInfo | null = null;

      const existingDoc = (userInfo as any)?.profileDocument;
      const email = userInfo.userName || "";
      const prefix = email.includes("@") ? email.split("@")[0] : email;

      const extension = pendingFile.name.split(".").pop() || "png";
      const base64File = await convertBase64(pendingFile);
      profileDocument = {
        documentId: existingDoc?.documentId || 0,
        documentName: `${prefix}.${extension}`,
        type: "PROFILE",
        fileType: pendingFile.type,
        content: stripDataUrlPrefix(base64File),
      };

      const data: UserprofilePictureObj = {
        userId: userInfo.userId,
        email: userInfo.userName,
        profileDocument,
      };

      await dispatch(saveUserProfilePicture(data));

      await dispatch(
        updateElementInAuthSlice({
          elementName: "userInfo",
          value: { ...userInfo, profileDocument },
        })
      );

      setFile(pendingFile);
    } catch (err) {
      console.error("Failed to save profile:", err);
    } finally {
      setIsConfirmationOpen(false);
      setPendingFile(null);
    }
  };

  const handleRemove = () => {
    setIsRemoveConfirmOpen(true);
  };

  const confirmRemove = async () => {
    const data: UserprofilePictureObj = {
      userId: userInfo.userId,
      email: userInfo.userName,
      profileDocument: null,
    };

    try {
      await dispatch(saveUserProfilePicture(data));
      await dispatch(
        updateElementInAuthSlice({
          elementName: "userInfo",
          value: { ...userInfo, profileDocument: null },
        })
      );
      setFile(null);
      setInitialSrc(undefined);
    } catch (err) {
      console.error("Failed to remove profile picture:", err);
    } finally {
      setIsRemoveConfirmOpen(false);
    }
  };

  const cancelRemove = () => {
    setIsRemoveConfirmOpen(false);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor:
          theme.palette.mode === "dark" ? "#1A1C23" : "#ffffff",
        p: { xs: 1, sm: 1.5, md: 2 },
        borderRadius: { xs: 2, md: 3 },
        border: `1px solid ${
          theme.palette.mode === "dark" ? "#2A2D38" : "#e5e9ed"
        }`,
        width: "100%",
      }}
    >
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center"
        gap={{ xs: 1, sm: 1.5, md: 2 }}
      >
        <Box
          sx={{
            borderRadius: { xs: 1.5, md: 2 },
            padding: { xs: 0.5, md: 1 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: colors.grey[700],
            width: { xs: 40, sm: 52, md: 64 },
            height: { xs: 40, sm: 52, md: 64 },
            minWidth: { xs: 40, sm: 52, md: 64 },
            flexShrink: 0,
          }}
          onClickCapture={isRestrictedUser ? (e) => { e.stopPropagation(); withPRUGuard(() => {})(); } : undefined}
        >
          <ProfileImageUpload
            value={file}
            onChange={handleProfileChange}
            maxSizeMB={5}
            outputType="image/png"
            initialSrc={initialSrc}
            onRemove={handleRemove}
            size={70}
            shape="square"
            hideHelper
            onSave={onSaveRequest}
            showBorder={false}
            preventImmediateClearOnRemove
          />
        </Box>

        <Box 
          sx={{ 
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <Typography
            variant="h5"
            textAlign="right"
            sx={{ 
              color: colors.grey[100],
              fontWeight: "bold",
              fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {fullName(
              null,
              userInfo.firstName,
              userInfo.middleName,
              userInfo.lastName
            )}
          </Typography>
          {userInfo.roleDisplayValue && (
            <Box
              component="span"
              sx={{
                display: "inline-block",
                maxWidth: "100%",
                mt: 0.3,
                mb: 0.3,
                px: { xs: 1, md: 1.25 },
                py: { xs: 0.2, md: 0.3 },
                borderRadius: "999px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(20,184,166,0.14)"
                    : "rgba(20,184,166,0.1)",
                color: colors.primary[300],
                fontWeight: 700,
                fontSize: { xs: "0.6rem", sm: "0.65rem", md: "0.7rem" },
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                lineHeight: 1.3,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {userInfo.roleDisplayValue}
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-end",
              columnGap: { xs: 0.5, md: 1 },
              rowGap: 0,
              fontWeight: "bold",
              fontSize: { xs: "0.65rem", sm: "0.7rem", md: "0.75rem" },
              lineHeight: 1.4,
            }}
          >
            <Box
              component="span"
              sx={{
                color: colors.grey[500],
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              {time.format("dddd, MMM D")}
            </Box>
            <Box
              component="span"
              sx={{ 
                color: colors.grey[300],
                whiteSpace: "nowrap",
              }}
            >
              {time.format("hh:mm A")}
            </Box>
          </Box>
        </Box>
      </Box>

      <OptionsPopup
        open={isConfirmationOpen}
        onClose={handleCancel}
        variant="confirm"
        message="Save this profile picture?"
        buttons={[
          {
            name: "Confirm",
            color: "primary",
            onClick: handleSaveProfilePicture,
          },
          { name: "Cancel", color: "secondary", onClick: handleCancel },
        ]}
      />
      <OptionsPopup
        open={isRemoveConfirmOpen}
        onClose={cancelRemove}
        variant="confirm"
        message="Do you really want to remove your profile picture?"
        buttons={[
          { name: "Remove", color: "error", onClick: confirmRemove },
          { name: "Cancel", color: "secondary", onClick: cancelRemove },
        ]}
      />
      {showPRUWarning.show && (
        <WarningPopupNew
          open={showPRUWarning.show}
          onClose={() => setShowPRUWarning({ show: false, message: '' })}
          title={showPRUWarning.title}
          message={<>{showPRUWarning.message}</>}
          variant={showPRUWarning.variant}
          buttons={showPRUWarning.buttons}
        />
      )}
      <CompletePracticeRegistrationModal
        open={registrationModalOpen}
        onClose={() => setRegistrationModalOpen(false)}
      />
    </Paper>
  );
};

export default TimePanel;